// Resolves an opaque storage key to a URL the browser can fetch.
//
// Production storage keys are short opaque identifiers (e.g.
// `members/<id>/<uuid>`). They are routed through the API so it can
// presign / proxy / authorise the underlying object-store fetch.
//
// Seed and demo data may store a fully-qualified URL directly in the
// same column (e.g. `https://images.unsplash.com/photo-...`). Those are
// already directly fetchable, so we pass them through unchanged.
//
// One module owns this rule so every projection that surfaces a member
// photo / group cover / event hero URL agrees on it. Lives in shared so
// apps/web (server components, mappers) and apps/server (projections)
// import the same source.
const ABSOLUTE_URL_PREFIXES = ['https://', 'http://'];
export function isAbsoluteMediaUrl(value) {
    return ABSOLUTE_URL_PREFIXES.some((p) => value.startsWith(p));
}
export function mediaUrlFromKey(key) {
    if (key === null)
        return null;
    if (isAbsoluteMediaUrl(key))
        return key;
    return `/api/v1/media/${encodeURIComponent(key)}`;
}
export function photoUrlFromKey(key, status) {
    if (key === null)
        return null;
    if (isAbsoluteMediaUrl(key))
        return key;
    if (status !== 'available')
        return null;
    return `/api/v1/media/${encodeURIComponent(key)}`;
}
// Size variants. Renditions are stored under deterministic sibling keys
// (`<key>/thumb`, `<key>/card`) written at upload-commit time — the same
// scheme as the PDF page-1 thumbnail (`<key>/thumb.jpg`). The variant is
// part of the storage key (path-encoded, never a query param) because the
// CDN cache policy for /api/v1/media/* excludes query strings from the
// cache key. Keys committed before variant generation existed serve the
// base object through the variant URL via the get-media fallback, so
// minting a variant URL is always safe.
//
//   thumb — 256px wide: avatars, logos, search hits, link-preview images
//   card  — 960px wide: card/list covers, feed gallery tiles
//   (full — the base key: detail heroes, lightbox, pre-crop originals)
export const MEDIA_VARIANTS = ['thumb', 'card'];
export function mediaVariantKey(key, variant) {
    return `${key}/${variant}`;
}
export function mediaVariantUrlFromKey(key, variant) {
    if (key === null)
        return null;
    if (isAbsoluteMediaUrl(key))
        return key;
    return mediaUrlFromKey(mediaVariantKey(key, variant));
}
export function photoVariantUrlFromKey(key, status, variant) {
    if (key === null)
        return null;
    if (isAbsoluteMediaUrl(key))
        return key;
    if (status !== 'available')
        return null;
    return mediaUrlFromKey(mediaVariantKey(key, variant));
}
const MEDIA_URL_PREFIX = '/api/v1/media/';
// For columns that store the already-projected `/api/v1/media/<enc>` string
// (resources.cover_image_url, learning_tracks.cover_image_url) rather than a
// storage key. Appends the percent-encoded variant suffix to the encoded key
// segment; absolute URLs and anything else pass through unchanged.
export function withMediaVariant(url, variant) {
    if (url === null)
        return null;
    if (!url.startsWith(MEDIA_URL_PREFIX))
        return url;
    return `${url}%2F${variant}`;
}
//# sourceMappingURL=media-url.js.map